import React, { FC, Fragment } from 'react';

import get from 'lodash/get';
import { nanoid } from '@reduxjs/toolkit';
import { FieldArray } from 'react-final-form-arrays';
import { useForm, useFormState } from 'react-final-form';

import { Box } from '@vk-hr-tek/ui/Box';
import { useInject } from '@vk-hr-tek/core/ioc';
import { SelectInput } from '@vk-hr-tek/app-ui/form';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { renderOptionWithIcon } from '@vk-hr-tek/ui/input';
import { ValidationService } from '@vk-hr-tek/core/validation';

import {
  ContentSurveysFormAnswerTypes,
  ContentSurveysFormValuesAnswerSetting,
  ContentSurveysFormValuesAnswerSettings,
} from '../../../../types';
import { ANSWER_OPTIONS } from '../../../../constants';

import { OneAnswer } from './OneAnswer';
import { TextAnswer } from './TextAnswer';
import { SeveralAnswer } from './SeveralAnswer';

interface AnswersProps {
  questionName: string;
}

export const Answers = ({ questionName }: AnswersProps) => {
  const validator = useInject(ValidationService);

  const { change } = useForm();
  const { values } = useFormState({
    subscription: {
      values: true,
    },
  });

  const renderAnswerInputByType: Record<
    ContentSurveysFormAnswerTypes,
    FC<{ name: string }>
  > = {
    one: OneAnswer,
    text: TextAnswer,
    several: SeveralAnswer,
  };

  const handleChangeAnswerType =
    (name: string) => (nextType: ContentSurveysFormAnswerTypes) => {
      const initialAnswersByType: Record<
        ContentSurveysFormAnswerTypes,
        ContentSurveysFormValuesAnswerSetting
      > = {
        one: {
          id: nanoid(),
          type: nextType,
          answers: [
            { content: '', id: nanoid() },
            { content: '', id: nanoid() },
          ],
        },
        several: {
          id: nanoid(),
          type: nextType,
          answers: [
            { content: '', id: nanoid() },
            { content: '', id: nanoid() },
          ],
        },
        text: {
          id: nanoid(),
          type: nextType,
          answer: { minLength: '', maxLength: '', textLimit: true },
        },
      };

      change(name, initialAnswersByType[nextType]);
    };

  return (
    <Box px="24" display="flex" flexDirection="column">
      <Box pb="12">
        <Typography variant="subtitle2">
          Ответ{' '}
          <Typography component="span" color="original.brand.primary">
            *
          </Typography>
        </Typography>
      </Box>

      <FieldArray<ContentSurveysFormValuesAnswerSettings>
        name={`${questionName}.answerSettings`}
      >
        {({ fields: questionFields }) =>
          questionFields.map((_, answerIndex) => {
            const name = `${questionName}.answerSettings[${answerIndex}]`;
            const typeName = `${questionName}.answerSettings[${answerIndex}].type`;

            const type = get(values, typeName, '');
            const AnswerComponent = type
              ? renderAnswerInputByType[type as ContentSurveysFormAnswerTypes]
              : null;

            return (
              <Fragment key={answerIndex}>
                <SelectInput
                  required
                  name={typeName}
                  items={ANSWER_OPTIONS}
                  placeholder="Выберите тип ответа"
                  renderOption={renderOptionWithIcon}
                  onChange={handleChangeAnswerType(name)}
                  validate={validator.required('Обязательное поле')}
                />

                {AnswerComponent && <AnswerComponent name={name} />}
              </Fragment>
            );
          })
        }
      </FieldArray>
    </Box>
  );
};
